import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Tooltip, ButtonGroup } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReplayIcon from "@mui/icons-material/Replay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";
import { useTheme } from "styled-components";
import { CellRendererProps } from "./types";
import { PrintIcon } from "../../assets/iconComponents/PrintIcon";
import { DnaIcon } from "../../assets/iconComponents/DnaIcon";
import {
  RequisitionNameWrapper,
  RequisitionActionBar,
  RequisitionActionButton,
  RequisitionTooltipText,
} from "./RequisitionActionCell.styles";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { getOnlineOrderPrintout } from "../NewRequisition/Services/NewReqService";
import { printPdfFromBlob } from "../../utils/printUtils";
import { REQUISITION_URL } from "../../utils/urlConstants";
import { ACTION_TOOLTIP_PROPS } from "../NewRequisition/OrderedRowActionBar";

const isArchivable = (reportedDate: string): boolean => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return new Date(reportedDate) < thirtyDaysAgo;
};

export const RequisitionActionCell: React.FC<CellRendererProps> = ({
  data,
  tabType,
  deviceType,
  onArchive,
  onUnarchive,
  onPrintError,
  onRequestReportUpdate,
  context,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { physicalDeviceType } = useDeviceHook();

  const isArchived = tabType === "ARCHIVED";
  const canArchive = isArchivable(data.reported);

  const handleMobileNavigation = () => {
    if (deviceType !== "desktop") {
      localStorage?.setItem(
        `btoProviderCurrentPage${tabType}`,
        JSON.stringify(context?.currentPage ?? 0),
      );
      if (data.sfID) {
        history.push(`${REQUISITION_URL}/${data.sfID}`);
      }
    }
  };

  const handlePrint = async () => {
    if (!auth || !data.unityCompleteId || isPrintLoading) return;
    setIsPrintLoading(true);
    try {
      const pdfUrl = await getOnlineOrderPrintout(
        auth.token,
        data.unityCompleteId,
        setLoggedOut,
      );
      if (pdfUrl) {
        const pdfResponse = await fetch(pdfUrl);
        if (!pdfResponse.ok) {
          onPrintError?.("Failed to print order - download error");
          setIsPrintLoading(false);
          return;
        }
        const blob = await pdfResponse.blob();
        const filename = `${data.name.replace(/\s+/g, "_")}_order.pdf`;
        await printPdfFromBlob(blob, filename, physicalDeviceType);
      } else {
        onPrintError?.("Failed to print order - not available");
      }
    } catch {
      onPrintError?.("Failed to print order");
    }
    setIsPrintLoading(false);
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canArchive && onArchive) {
      onArchive(data.sfID);
    }
  };

  const handleUnarchive = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onUnarchive) {
      onUnarchive(data.sfID);
    }
  };

  // Only show action bar on COMPLETED and ARCHIVED tabs.
  // Priority: Request update, Archive/Unarchive, then Print.
  const showActionBar = tabType === "COMPLETED" || tabType === "ARCHIVED";
  let requestReportUpdateLabel = "Request updates to this report";
  if (data.reportUpdate?.is_pending) {
    requestReportUpdateLabel = "An update is already in review.";
  } else if (!data.canRequestReportUpdate) {
    requestReportUpdateLabel = "Reports can't be updated after the due date.";
  }

  return (
    <RequisitionNameWrapper onClick={handleMobileNavigation}>
      <div className="grid-name-cell">
        {data.missingInfo && (
          <FontAwesomeIcon icon={faCircleExclamation} color={theme.redDark} />
        )}
        {data.name}
      </div>
      {showActionBar && (
        <RequisitionActionBar
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <ButtonGroup variant="contained" size="small">
            {tabType === "COMPLETED" && onRequestReportUpdate && (
              <Tooltip
                {...ACTION_TOOLTIP_PROPS}
                componentsProps={{ tooltip: { sx: { maxWidth: 350 } } }}
                title={
                  <RequisitionTooltipText>
                    {requestReportUpdateLabel}
                  </RequisitionTooltipText>
                }
              >
                <span>
                  <RequisitionActionButton
                    aria-label={requestReportUpdateLabel}
                    onClick={() => onRequestReportUpdate(data)}
                    disabled={!data.canRequestReportUpdate}
                    $isDisabled={!data.canRequestReportUpdate}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} fontSize={16} />
                  </RequisitionActionButton>
                </span>
              </Tooltip>
            )}
            {isArchived ? (
              <Tooltip
                {...ACTION_TOOLTIP_PROPS}
                title={
                  <RequisitionTooltipText>
                    Un-archive this order
                  </RequisitionTooltipText>
                }
              >
                <RequisitionActionButton
                  aria-label="Un-archive this order"
                  onClick={handleUnarchive}
                >
                  <ReplayIcon
                    sx={{ fontSize: 20, transform: "rotate(-45deg)" }}
                  />
                </RequisitionActionButton>
              </Tooltip>
            ) : (
              <Tooltip
                {...ACTION_TOOLTIP_PROPS}
                componentsProps={{ tooltip: { sx: { maxWidth: 350 } } }}
                title={
                  <RequisitionTooltipText>
                    {canArchive
                      ? "Archive this order"
                      : "Orders must be older than 30 days to be archived"}
                  </RequisitionTooltipText>
                }
              >
                <span>
                  <RequisitionActionButton
                    aria-label={
                      canArchive
                        ? "Archive this order"
                        : "Orders must be older than 30 days to be archived"
                    }
                    onClick={handleArchive}
                    disabled={!canArchive}
                    $isDisabled={!canArchive}
                  >
                    <InventoryIcon sx={{ fontSize: 18 }} />
                  </RequisitionActionButton>
                </span>
              </Tooltip>
            )}
            {data.unityCompleteId && (
              <Tooltip
                {...ACTION_TOOLTIP_PROPS}
                title={
                  <RequisitionTooltipText>
                    Print this order
                  </RequisitionTooltipText>
                }
              >
                <RequisitionActionButton
                  aria-label="Print this order"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePrint();
                  }}
                  disabled={isPrintLoading}
                >
                  {isPrintLoading ? (
                    <DnaIcon color={theme.redMedium} />
                  ) : (
                    <PrintIcon width={16} height={16} />
                  )}
                </RequisitionActionButton>
              </Tooltip>
            )}
          </ButtonGroup>
        </RequisitionActionBar>
      )}
    </RequisitionNameWrapper>
  );
};
